import { Badge, Group, Stack, Table, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import type { Enums, Tables } from "@how/database";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PermissionDenied,
  RecordDrawer,
  StatusBadge,
} from "@how/ui";
import { isAuthorizationError } from "./query-client";
import { useSupabase } from "./session";

export interface SupportSession {
  readonly id: string;
  readonly tenantId: string;
  readonly platformUserId: string;
  readonly purpose: string;
  readonly accessLevel: Enums<"support_access_level">;
  readonly status: Enums<"support_session_status">;
  readonly grantedPermissions: readonly string[];
  readonly requestedAt: string;
  readonly requestedDurationMinutes: number;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly endedAt: string | null;
  readonly approvedByUserId: string | null;
  readonly breakGlass: boolean;
  readonly breakGlassJustification: string | null;
}

type Row = Pick<
  Tables<"support_sessions">,
  | "id"
  | "tenant_id"
  | "platform_user_id"
  | "purpose"
  | "access_level"
  | "status"
  | "granted_permissions"
  | "requested_at"
  | "requested_duration_minutes"
  | "starts_at"
  | "ends_at"
  | "ended_at"
  | "approved_by_user_id"
  | "break_glass"
  | "break_glass_justification"
>;

const COLUMNS =
  "id, tenant_id, platform_user_id, purpose, access_level, status, granted_permissions, requested_at, requested_duration_minutes, starts_at, ends_at, ended_at, approved_by_user_id, break_glass, break_glass_justification";

function toSession(row: Row): SupportSession {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    platformUserId: row.platform_user_id,
    purpose: row.purpose,
    accessLevel: row.access_level,
    status: row.status,
    grantedPermissions: row.granted_permissions,
    requestedAt: row.requested_at,
    requestedDurationMinutes: row.requested_duration_minutes,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    endedAt: row.ended_at,
    approvedByUserId: row.approved_by_user_id,
    breakGlass: row.break_glass,
    breakGlassJustification: row.break_glass_justification,
  };
}

/** Sessions the caller may see under RLS: their own, their tenant's (settings.manage), or all (platform admin). */
export function useSupportSessions(filter: { tenantId?: string } = {}) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: ["support-sessions", filter.tenantId ?? "all"],
    queryFn: async (): Promise<SupportSession[]> => {
      let query = supabase
        .from("support_sessions")
        .select(COLUMNS)
        .order("requested_at", { ascending: false })
        .limit(100);
      if (filter.tenantId !== undefined) query = query.eq("tenant_id", filter.tenantId);
      const { data, error } = await query;
      if (error) throw error;
      return (data as Row[]).map(toSession);
    },
  });
}

export interface RpcErrorDescription {
  readonly kind: "denied" | "invalid" | "missing" | "conflict" | "unknown";
  readonly message: string;
}

/** Maps the RPC error contract (42501, 23514, P0002, 23505) to a user-facing description. */
export function describeRpcError(error: unknown): RpcErrorDescription {
  const e = (typeof error === "object" && error !== null ? error : {}) as {
    code?: unknown;
    message?: unknown;
  };
  const message =
    typeof e.message === "string" && e.message.length > 0 ? e.message : "Something went wrong.";
  if (e.code === "42501" || isAuthorizationError(error)) return { kind: "denied", message };
  if (e.code === "23514") return { kind: "invalid", message };
  if (e.code === "P0002") return { kind: "missing", message };
  if (e.code === "23505") return { kind: "conflict", message };
  return { kind: "unknown", message };
}

function notifyRpcError(title: string, error: unknown): void {
  const described = describeRpcError(error);
  notifications.show({
    color: described.kind === "denied" ? "yellow" : "red",
    title,
    message: described.message,
  });
}

export interface RequestSupportSessionInput {
  readonly tenantId: string;
  readonly purpose: string;
  readonly accessLevel: Enums<"support_access_level">;
  readonly durationMinutes: number;
  /** Explicit permission scope; omitted = every non-sensitive permission. */
  readonly permissions?: readonly string[];
}

/** The four RPCs as mutations; each refreshes the session list and reports errors as notifications. */
export function useSupportSessionMutations() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["support-sessions"] });
    void queryClient.invalidateQueries({ queryKey: ["platform-stats"] });
  };
  const request = useMutation({
    mutationFn: async (input: RequestSupportSessionInput): Promise<string> => {
      const { data, error } = await supabase.rpc("request_support_session", {
        p_tenant_id: input.tenantId,
        p_purpose: input.purpose,
        p_access_level: input.accessLevel,
        p_duration_minutes: input.durationMinutes,
        ...(input.permissions === undefined ? {} : { p_permissions: [...input.permissions] }),
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_id, input) => {
      notifications.show({
        color: "teal",
        title: input.accessLevel === "read_only" ? "Session started" : "Session requested",
        message:
          input.accessLevel === "read_only"
            ? "Read-only access is active for the requested window."
            : "The church's administrators can approve it now; a super admin can break glass.",
      });
      refresh();
    },
    onError: (error) => {
      notifyRpcError("Could not request the session", error);
    },
  });
  const approve = useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase.rpc("approve_support_session", { p_session_id: sessionId });
      if (error) throw error;
    },
    onSuccess: () => {
      notifications.show({
        color: "teal",
        title: "Approved",
        message: "The support session is active.",
      });
      refresh();
    },
    onError: (error) => {
      notifyRpcError("Could not approve", error);
    },
  });
  const breakGlass = useMutation({
    mutationFn: async (input: { sessionId: string; justification: string }) => {
      const { error } = await supabase.rpc("break_glass_support_session", {
        p_session_id: input.sessionId,
        p_justification: input.justification,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      notifications.show({
        color: "teal",
        title: "Break-glass approved",
        message: "The session is active and audited as break-glass.",
      });
      refresh();
    },
    onError: (error) => {
      notifyRpcError("Could not break glass", error);
    },
  });
  const end = useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase.rpc("end_support_session", { p_session_id: sessionId });
      if (error) throw error;
    },
    onSuccess: () => {
      notifications.show({ color: "teal", title: "Session closed", message: "Access ended." });
      refresh();
    },
    onError: (error) => {
      notifyRpcError("Could not close the session", error);
    },
  });
  return { request, approve, breakGlass, end };
}

export function formatWindow(session: SupportSession): string {
  if (session.startsAt === null || session.endsAt === null)
    return `${String(session.requestedDurationMinutes)} min once approved`;
  return `${new Date(session.startsAt).toLocaleString()} → ${new Date(session.endsAt).toLocaleTimeString()}`;
}

export interface SupportSessionsTableProps {
  readonly sessions: readonly SupportSession[] | undefined;
  readonly isPending: boolean;
  readonly error: unknown;
  readonly onRetry: () => void;
  readonly onSelect: (session: SupportSession) => void;
  /** Tenant names by id; shown when present (the control plane), otherwise the column is omitted. */
  readonly tenantNames?: ReadonlyMap<string, string>;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
}

/** The one support-session list: status, level, purpose, window; rows open the drawer. */
export function SupportSessionsTable({
  sessions,
  isPending,
  error,
  onRetry,
  onSelect,
  tenantNames,
  emptyTitle = "No support sessions",
  emptyDescription = "Sessions appear here as soon as they are requested.",
}: SupportSessionsTableProps) {
  if (isPending) return <LoadingState rows={4} />;
  if (error !== null && error !== undefined) {
    return isAuthorizationError(error) ? (
      <PermissionDenied />
    ) : (
      <ErrorState message="Could not load support sessions." onRetry={onRetry} />
    );
  }
  if (sessions === undefined || sessions.length === 0)
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  return (
    <Table.ScrollContainer minWidth={720}>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {tenantNames === undefined ? null : <Table.Th>Church</Table.Th>}
            <Table.Th>Purpose</Table.Th>
            <Table.Th>Access</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Requested</Table.Th>
            <Table.Th>Window</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sessions.map((s) => (
            <Table.Tr
              key={s.id}
              style={{ cursor: "pointer" }}
              onClick={() => {
                onSelect(s);
              }}
            >
              {tenantNames === undefined ? null : (
                <Table.Td>{tenantNames.get(s.tenantId) ?? s.tenantId.slice(0, 8)}</Table.Td>
              )}
              <Table.Td>
                <Text size="sm" truncate maw={320}>
                  {s.purpose}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap={4}>
                  <Badge
                    variant="light"
                    color={s.accessLevel === "read_write" ? "orange" : "gray"}
                    size="sm"
                  >
                    {s.accessLevel === "read_write" ? "read and write" : "read only"}
                  </Badge>
                  {s.breakGlass ? (
                    <Badge variant="light" color="red" size="sm">
                      break-glass
                    </Badge>
                  ) : null}
                </Group>
              </Table.Td>
              <Table.Td>
                <StatusBadge domain="support_session_status" value={s.status} />
              </Table.Td>
              <Table.Td>{new Date(s.requestedAt).toLocaleString()}</Table.Td>
              <Table.Td>{formatWindow(s)}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

export interface SupportSessionDrawerProps {
  readonly session: SupportSession | null;
  readonly onClose: () => void;
  readonly tenantName?: string;
  /** Buttons the current caller may use; the app decides which to offer, the database decides whether they work. */
  readonly actions?: ReactNode;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text size="sm" ta="right">
        {children}
      </Text>
    </Group>
  );
}

/** Session details: who, what for, which scope, which window, and how it was approved. */
export function SupportSessionDrawer({
  session,
  onClose,
  tenantName,
  actions,
}: SupportSessionDrawerProps) {
  return (
    <RecordDrawer
      opened={session !== null}
      onClose={onClose}
      title="Support session"
      subtitle={
        session === null ? undefined : (
          <StatusBadge domain="support_session_status" value={session.status} />
        )
      }
    >
      {session === null ? null : (
        <Stack gap="md">
          <Stack gap="xs">
            {tenantName === undefined ? null : <Field label="Church">{tenantName}</Field>}
            <Field label="Purpose">{session.purpose}</Field>
            <Field label="Access">
              {session.accessLevel === "read_write" ? "read and write" : "read only"}
            </Field>
            <Field label="Requested">{new Date(session.requestedAt).toLocaleString()}</Field>
            <Field label="Window">{formatWindow(session)}</Field>
            {session.endedAt === null ? null : (
              <Field label="Closed">{new Date(session.endedAt).toLocaleString()}</Field>
            )}
            {session.breakGlass ? (
              <Field label="Break-glass">{session.breakGlassJustification ?? "yes"}</Field>
            ) : null}
          </Stack>
          <Stack gap={4}>
            <Text size="sm" c="dimmed">
              Scope ({session.grantedPermissions.length} permissions)
            </Text>
            <Group gap={4}>
              {session.grantedPermissions.map((p) => (
                <Badge key={p} variant="light" color="gray" size="sm">
                  {p}
                </Badge>
              ))}
            </Group>
          </Stack>
          {actions === undefined ? null : <Group justify="flex-end">{actions}</Group>}
        </Stack>
      )}
    </RecordDrawer>
  );
}
